import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

export const RerunRanksCard = () => {
  const [preview, setPreview] = useState(null);
  const [run, setRun] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const poll = async () => {
    try {
      const { data } = await api.get("/scheduler/rerun-ranks/status");
      setRun(data?.id ? data : null);
      if (data?.status === "running") {
        timer.current = setTimeout(poll, 5000);
      } else if (data?.status === "completed" && timer.current) {
        toast.success("All client ranking checks finished.");
        timer.current = null;
      }
    } catch {
      timer.current = null;
    }
  };

  useEffect(() => {
    poll();
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openConfirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduler/rerun-ranks/preview");
      setPreview(data);
      setOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load client counts");
    } finally {
      setLoading(false);
    }
  };

  const start = async () => {
    try {
      const { data } = await api.post("/scheduler/rerun-ranks");
      setRun(data);
      toast.success("Ranking re-run started. Results arrive over the next few minutes.");
      timer.current = setTimeout(poll, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start the re-run");
    }
  };

  const running = run?.status === "running";

  return (
    <div className="card-soft p-5 space-y-4" data-testid="settings-rerun-ranks">
      <div className="flex items-center gap-2.5">
        <div className="h-7 w-7 rounded-lg bg-[#e6fffa] border border-[#81e6d9] flex items-center justify-center">
          <RefreshCw className="h-3.5 w-3.5 text-[#319795]" strokeWidth={2.4} />
        </div>
        <h2 className="font-display text-base text-[#1a202c]">Re-run all ranking checks</h2>
      </div>
      <p className="text-sm text-[#718096]">
        Forces a fresh rank check for every active client using the low-cost queue. Frozen clients are skipped.
      </p>
      {run && (
        <div className="text-xs text-[#4a5568] font-mono-tight" data-testid="rerun-ranks-status">
          {running ? "Running" : `Last run ${run.status}`}: {run.done}/{run.total} clients
          {run.failed ? ` (${run.failed} failed)` : ""}
          {run.started_at && ` · started ${new Date(run.started_at).toLocaleString()}`}
        </div>
      )}
      <button
        type="button"
        onClick={openConfirm}
        disabled={running || loading}
        data-testid="rerun-ranks-btn"
        className="inline-flex items-center gap-2 rounded-xl bg-[#1a202c] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {running || loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        {running ? "Re-run in progress" : "Re-run all ranking checks"}
      </button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="rerun-ranks-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-run all ranking checks?</AlertDialogTitle>
            <AlertDialogDescription data-testid="rerun-ranks-confirm-counts">
              This will check {preview?.keywords ?? 0} keywords across {preview?.clients ?? 0} active clients.
              {preview?.frozen_skipped ? ` ${preview.frozen_skipped} frozen client(s) will be skipped.` : ""}
              {" "}Every keyword is charged again on DataForSEO.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rerun-ranks-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={start} data-testid="rerun-ranks-confirm-btn">
              Run checks
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
